import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Wrench, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ExecutionStatusOverlay from './ExecutionStatusOverlay';
import { getExecutionBorderClass, type ExecutionStatus } from './executionStatus';

interface ToolCallNodeData {
  toolName: string;
  toolDescription?: string;
  mcpServer?: string;
  parameters?: Record<string, any>;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

function ToolCallNode({ data, selected }: NodeProps<ToolCallNodeData>) {
  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${
        selected ? 'border-cyan-500 shadow-cyan-200' : 'border-cyan-300'
      } ${getExecutionBorderClass(data.executionStatus)}`}
    >
      <ExecutionStatusOverlay status={data.executionStatus} />
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-cyan-500"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          <span className="font-semibold text-sm">Tool Call</span>
        </div>
        <div className="flex gap-1">
          {data.onEdit && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-cyan-600"
              onClick={data.onEdit}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
          {data.onDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-red-600"
              onClick={data.onDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div>
          <div className="font-semibold text-sm text-gray-900">{data.toolName}</div>
          {data.toolDescription && (
            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
              {data.toolDescription}
            </div>
          )}
        </div>

        {data.mcpServer && (
          <div className="pt-2 border-t">
            <Badge variant="secondary" className="text-xs">
              {data.mcpServer}
            </Badge>
          </div>
        )}

        {data.parameters && Object.keys(data.parameters).length > 0 && (
          <div className="text-xs text-gray-500">
            {Object.keys(data.parameters).length} parameter(s)
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-cyan-500"
      />
    </div>
  );
}

export default memo(ToolCallNode);

